import { useQuery, useMutation } from '@tanstack/react-query';
import { UserApi } from '@Apis';
import { TargetType } from '@Utils/enums';
import { Service } from '@Types/app';
import { TreeNode } from '@Components/Tree/type';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface SharePointTarget {
  id: string;
  name: string;
  displayName?: string;
  url?: string;
  email?: string;
  type?: string;
  title?: string;
  key?: string;
}

interface PaginationInfo {
  userOffset: number;
  userTotalCount: number;
  groupOffset: number;
  groupTotalCount: number;
  next: number;
}

interface SharePointTargetsResponse {
  targets: SharePointTarget[];
  targetGroups: SharePointTarget[];
  pagination?: PaginationInfo;
}

const getSharePointTargetsQueryKey = (orgId: string, searchString?: string) => [
  'sharePointTargets',
  orgId,
  searchString,
];

// Hook to manage SharePoint targets with pagination and search
export const useSharePointTargets = (
  orgId: string,
  enabled = true,
  searchString?: string
) => {
  const { t } = useTranslation();
  const [allTargets, setAllTargets] = useState<SharePointTarget[]>([]);
  const [allGroups, setAllGroups] = useState<SharePointTarget[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreTargets, setHasMoreTargets] = useState(false);
  const [hasMoreGroups, setHasMoreGroups] = useState(false);

  // Initial query to get first page
  const initialQuery = useQuery({
    queryKey: getSharePointTargetsQueryKey(orgId, searchString),
    queryFn: async () => {
      const service = Service.SharePoint;
      const targetType = TargetType.UserAndGroup;

      // Use search API if searchString is provided, otherwise use regular API
      if (searchString && searchString.trim()) {
        const response = await UserApi.searchTarget(
          searchString.trim(),
          orgId,
          service,
          targetType,
          undefined,
          undefined
        );
        return response.data as SharePointTargetsResponse;
      } else {
        // First request without pagination data
        const response = await UserApi.getPolicyTargets(
          orgId,
          service,
          targetType,
          '0',
          undefined,
          undefined
        );
        return response.data as SharePointTargetsResponse;
      }
    },
    enabled: enabled && !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation to load more data
  const loadMoreMutation = useMutation({
    mutationFn: async (paginationData: PaginationInfo) => {
      const service = Service.SharePoint;
      const targetType = TargetType.UserAndGroup;

      // Use search API if searchString is provided, otherwise use regular API
      if (searchString && searchString.trim()) {
        const response = await UserApi.searchTarget(
          searchString.trim(),
          orgId,
          service,
          targetType,
          paginationData,
          undefined
        );
        return response.data as SharePointTargetsResponse;
      } else {
        // Subsequent requests with pagination data
        const response = await UserApi.getPolicyTargets(
          orgId,
          service,
          targetType,
          '0',
          paginationData,
          undefined
        );
        return response.data as SharePointTargetsResponse;
      }
    },
    onSuccess: (data) => {
      if (data.targets) {
        setAllTargets((prev) => [...prev, ...data.targets]);
      }
      if (data.targetGroups) {
        setAllGroups((prev) => [...prev, ...data.targetGroups]);
      }
      if (data.pagination) {
        setPagination(data.pagination);
        updateHasMore(data.pagination);
      } else {
        // No pagination means we've reached the end
        setHasMoreTargets(false);
        setHasMoreGroups(false);
      }
      setIsLoadingMore(false);
    },
    onError: () => {
      setIsLoadingMore(false);
    },
  });

  // Update initial data when query succeeds
  useEffect(() => {
    if (initialQuery.data) {
      setAllTargets(initialQuery.data.targets || []);
      setAllGroups(initialQuery.data.targetGroups || []);
      if (initialQuery.data.pagination) {
        setPagination(initialQuery.data.pagination);
        updateHasMore(initialQuery.data.pagination);
      }
    }
  }, [initialQuery.data]);

  const updateHasMore = (paginationInfo: PaginationInfo) => {
    setHasMoreTargets(
      paginationInfo.userOffset < paginationInfo.userTotalCount
    );
    setHasMoreGroups(
      paginationInfo.groupOffset < paginationInfo.groupTotalCount
    );
  };

  // Load more function
  const loadMore = useCallback(() => {
    if (pagination && !isLoadingMore && (hasMoreTargets || hasMoreGroups)) {
      setIsLoadingMore(true);
      loadMoreMutation.mutate(pagination);
    }
  }, [
    pagination,
    isLoadingMore,
    hasMoreTargets,
    hasMoreGroups,
    loadMoreMutation,
  ]);

  // Transform to tree nodes
  const transformToTreeNodes = useCallback((): TreeNode[] => {
    const nodes: TreeNode[] = [];

    // Create "All sites" root node
    const allSitesChildren: TreeNode[] = [];

    // Add individual sites
    allTargets.forEach((target) => {
      allSitesChildren.push({
        key: target.url || target.email || target.key || target.id,
        title:
          target.name ||
          target.displayName ||
          target.title ||
          'SharePoint Site',
        isLeaf: true,
        data: target,
      });
    });

    // Add groups
    allGroups.forEach((group) => {
      allSitesChildren.push({
        key: group.url || group.email || group.key || group.id,
        title:
          group.name || group.displayName || group.title || 'SharePoint Group',
        isLeaf: true,
        data: group,
      });
    });

    // Add "Show more" node if there are more items
    if (hasMoreTargets || hasMoreGroups) {
      allSitesChildren.push({
        key: 'show-more',
        title: isLoadingMore
          ? t('common.text.loading')
          : t('policy.node.showMore'),
        isLeaf: true,
        disableCheckbox: true,
        onSelect: loadMore,
        isShowMore: true,
      });
    }

    nodes.push({
      key: 'all-sites',
      title: t('policy.policyTarget.sharePoint.all'),
      children: allSitesChildren,
      expanded: true, // Make this node expanded by default
    });

    return nodes;
  }, [
    allTargets,
    allGroups,
    hasMoreTargets,
    hasMoreGroups,
    isLoadingMore,
    t,
    loadMore,
  ]);

  return {
    data: transformToTreeNodes(),
    isLoading: initialQuery.isLoading,
    isLoadingMore,
    error: initialQuery.error,
    refetch: initialQuery.refetch,
    loadMore,
  };
};
